import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { Currency } from "@przezmorze/api-types";
import { isNil, sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { usePackages } from "../../admin/packages/usePackages";
import type { ProductResponse } from "../../admin/products/productsApi";
import { useAuthContext } from "../../auth/AuthContext";
import type { CalculationProduct } from "../../calculations/calculationTypes";
import DatePicker from "../../components/datePicker/DatePicker";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import { removeArr, toNumber, updateArr } from "../../misc/utils";
import OptionPriceEditDialog from "../../sharedComponents/optionPrice/OptionPriceEditDialog";
import type { Product } from "../../sharedComponents/products/ProductSelect";
import { useAsyncDialog } from "../../sharedComponents/useAsyncDialog";
import type { CalculationDraft } from "../CalculationDraft";
import { ProductSelectAction } from "../components/ProductSelectAction";
import { ProviderProductIcon } from "../components/ProviderProductIcon";
import { AlternativesProductsDialog } from "../dialogs/AlternativesProductsDialog";
import { ProductInfoDialog } from "../dialogs/ProductInfoDialog";
import { formatDateWithDay } from "../generic/formatDateWithDay";
import { currencyForProduct } from "../utils/currencyForProduct";
import { nextDate } from "../utils/nextDate";
import { AddOptionRow } from "./productList/AddOptionRow";
import type { useCalculationProducts } from "./useCalculationProducts";

type ProductWithPrices = ReturnType<typeof useCalculationProducts>["productsWithPrices"][number];

type GroupedProduct = {
  product: ProductWithPrices;
  idx: number;
};

type ProductGroup = {
  date: string;
  products: GroupedProduct[];
};

function groupProductsByDate(products: ProductWithPrices[]): ProductGroup[] {
  return Object.values(
    products.reduce<Record<string, ProductGroup>>((acc, product, idx) => {
      const key = dayjs(product.date).format("YYYY-MM-DD");

      if (!acc[key]) {
        acc[key] = {
          date: product.date,
          products: [],
        };
      }

      acc[key].products.push({ product, idx });

      return acc;
    }, {}),
  );
}

export function ProductList({
  draft,
  onChange,
  onProductReplace,
  calculationProducts,
  isReadOnly,
  hasDynamicPriceProducts,
}: {
  draft: CalculationDraft;
  onProductReplace: (productId: string, replacement: CalculationProduct) => void;
  onChange: (change: Partial<Pick<CalculationDraft, "products" | "currency">>) => void;
  calculationProducts: ReturnType<typeof useCalculationProducts>;
  isReadOnly: boolean;
  hasDynamicPriceProducts: boolean;
}) {
  const { currency, products } = draft;
  const { startDate } = draft.basicData;
  const [infoDialogProduct, setInfoDialogProduct] = useState<ProductResponse>();
  const alternativesDialog = useAsyncDialog<
    void,
    {
      selectedProduct: CalculationProduct;
      calculationCurrency: NonNullable<typeof currency>;
      calculationMargin: number;
      participantsNumber: number;
    }
  >(({ onCancel, arg }) =>
    arg ? <AlternativesProductsDialog {...arg} onClose={onCancel} onProductReplace={onProductReplace} /> : null,
  );
  const { productDetailsMap, availableProducts, productsWithPrices } = calculationProducts;
  const { packages } = usePackages();
  const packageProductOptionalMap = useMemo<Map<string, boolean>>(() => {
    if (!draft.packageId) {
      return new Map();
    }
    const packageDays = packages?.find((p) => p.id.id === draft.packageId)?.days ?? [];
    return new Map(packageDays.flatMap((d) => d.products.map((p) => [p.id, p.optional])));
  }, [packages, draft.packageId]);
  const { context } = useAuthContext();
  const canEditPrice =
    !isReadOnly &&
    (context?.role === "ADMIN" || (context?.role === "AGENT" && !context?.customPrivileges.cannotChangeOptionPrices));
  const [editingDateIdx, setEditingDateIdx] = useState<number | null>(null);
  const [editingOption, setEditingOption] = useState<{ productIdx: number; optionId: string } | null>(null);

  const handleDateChange = useCallback(
    (idx: number, newDate: string) => {
      const parsed = dayjs(newDate, ISO_DATE_FORMAT);
      if (!parsed.isValid()) return;
      const product = products[idx];
      onChange({
        products: sortBy(
          updateArr(products, idx, { ...product, daysShift: parsed.diff(startDate, "days") }),
          (p) => p.daysShift,
        ),
      });
      setEditingDateIdx(null);
    },
    [startDate, products, onChange],
  );

  const handleOptionPriceChange = useCallback(
    (productIdx: number, optionId: string, newPrice: PriceValue) => {
      const product = products[productIdx];
      onChange({
        products: updateArr(products, productIdx, {
          ...product,
          options: product.options.map((o) => (o.id === optionId ? { ...o, price: newPrice } : o)),
        }),
      });
      setEditingOption(null);
    },
    [products, onChange],
  );

  const handleAddProduct = useCallback(
    (product: { id: string; date: string }) => {
      const newProduct = {
        ...product,
        daysShift: dayjs(product.date, ISO_DATE_FORMAT).diff(startDate, "days"),
        options: [],
      };
      const selectedCurrency = currency ?? currencyForProduct(availableProducts, product.id);
      onChange({
        currency: selectedCurrency,
        products: sortBy(updateArr(products, products.length, newProduct), (prd) => prd.daysShift),
      });
    },
    [startDate, products, currency, availableProducts, onChange],
  );

  const handleQtyChange = useCallback(
    (productIdx: number, optionId: string, quantity: number) => {
      const product = products[productIdx];
      const updatedOptions = product.options.map((o) => (o.id === optionId ? { ...o, quantity } : o));
      onChange({ products: updateArr(products, productIdx, { ...product, options: updatedOptions }) });
    },
    [products, onChange],
  );

  const handleRemoveOption = useCallback(
    (productIdx: number, optionId: string) => {
      const product = products[productIdx];
      const updatedOptions = product.options.filter((o) => o.id !== optionId);
      onChange({ products: updateArr(products, productIdx, { ...product, options: updatedOptions }) });
    },
    [products, onChange],
  );

  const handleAddOption = useCallback(
    (productIdx: number, option: { id: string; quantity: number }) => {
      const product = products[productIdx];
      onChange({ products: updateArr(products, productIdx, { ...product, options: [...product.options, option] }) });
    },
    [products, onChange],
  );

  function showAlternatives(product: ProductWithPrices) {
    if (!currency) {
      return;
    }
    void alternativesDialog.open({
      selectedProduct: { id: product.id, date: product.date, options: [] },
      calculationCurrency: currency,
      calculationMargin: draft.basicData.margin,
      participantsNumber: draft.basicData.peopleCount + draft.basicData.freePeopleCount,
    });
  }

  const groupedProducts = groupProductsByDate(productsWithPrices);

  return (
    <Grid size={{ xs: 12, lg: 9 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 3,
        }}
      >
        Produkty w pakiecie ({products.length})
      </Typography>
      <Stack
        spacing={3}
        sx={{ ml: { xs: 2, md: 3 }, borderLeft: "2px dashed #e0e0e0", pl: { xs: 3, md: 5 }, position: "relative" }}
      >
        {groupedProducts.map((productGroup) => {
          return (
            <Box key={productGroup.date} data-testid="product-date-row" sx={{ mb: 5, position: "relative" }}>
              <Box
                sx={{
                  position: "absolute",
                  left: { xs: -25, md: -41 },
                  transform: "translateX(-50%)",
                  top: 4,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  border: "3px solid #fff",
                  boxShadow: "0 0 0 1px #ccc",
                }}
              />
              <Typography
                component="span"
                variant="subtitle1"
                sx={{
                  display: "inline-block",
                  color: "text.primary",
                  fontWeight: "bold",
                  bgcolor: "rgba(0,0,0,0.04)",
                  px: 1,
                  py: 0.3,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                {formatDateWithDay(productGroup.date)}
              </Typography>
              {productGroup.products.map((groupedProduct) => {
                const product = groupedProduct.product;
                const idx = groupedProduct.idx;
                const details = productDetailsMap.get(product.id);
                const optionalFlag = packageProductOptionalMap.get(product.id);
                const isPackageProduct = packageProductOptionalMap.has(product.id);
                const isOptional = optionalFlag === true;
                const isMandatory = isPackageProduct && !isOptional;
                return (
                  <Box key={`${product.id}:${idx}`} sx={{ mb: 5, position: "relative" }}>
                    <Paper
                      key={`${product.id}:${idx}`}
                      elevation={0}
                      sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(0,0,0,0.02)",
                          borderBottom: "1px solid #e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{
                            alignItems: "center",
                          }}
                        >
                          {details?.provider.productType && <ProviderProductIcon type={details.provider.productType} />}
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: "bold",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              {details?.name}
                              {isOptional && (
                                <Chip label="Produkt opcjonalny" size="small" variant="outlined" color="default" />
                              )}
                              {editingDateIdx === idx ? (
                                <DatePicker
                                  value={product.date}
                                  onChange={(date) => handleDateChange(idx, date)}
                                  onBlur={() => setEditingDateIdx(null)}
                                  size="small"
                                  autoFocus
                                  sx={{ width: 160 }}
                                />
                              ) : (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  onClick={() => !isReadOnly && setEditingDateIdx(idx)}
                                  sx={{
                                    color: "text.primary",
                                    fontWeight: "bold",
                                    bgcolor: "rgba(0,0,0,0.04)",
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: 1,
                                    cursor: !isReadOnly ? "pointer" : "auto",
                                    "&:hover": { bgcolor: !isReadOnly ? "rgba(0,0,0,0.04)" : undefined },
                                  }}
                                >
                                  {formatDateWithDay(product.date)}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Pokaż alternatywne produkty (porównanie cen)">
                            <IconButton
                              size="small"
                              sx={{ color: "primary.main", bgcolor: "rgba(13, 110, 253, 0.04)" }}
                              onClick={() => showAlternatives(product)}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Informacje o produkcie">
                            <IconButton
                              size="small"
                              sx={{ color: "text.secondary" }}
                              onClick={() => setInfoDialogProduct(details ?? undefined)}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={isMandatory ? "Produkt obowiązkowy – nie można usunąć" : "Usuń produkt z kalkulacji"}
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={isMandatory || isReadOnly}
                                sx={{ color: isMandatory ? "text.disabled" : "text.secondary" }}
                                onClick={() => onChange({ products: removeArr(products, idx) })}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: "text.secondary", fontWeight: "bold" }}>
                                Wybrana opcja (Wariant)
                              </TableCell>
                              <TableCell sx={{ color: "text.secondary", fontWeight: "bold", width: 100 }}>
                                Cena jedn.
                              </TableCell>
                              <TableCell sx={{ color: "text.secondary", fontWeight: "bold", width: 150 }}>
                                Ilość sztuk
                              </TableCell>
                              {hasDynamicPriceProducts && (
                                <TableCell sx={{ color: "text.secondary", fontWeight: "bold", width: 150 }}>
                                  Dostępna ilość
                                </TableCell>
                              )}
                              <TableCell
                                sx={{ color: "text.secondary", fontWeight: "bold", width: 100, textAlign: "right" }}
                              >
                                Suma
                              </TableCell>
                              <TableCell sx={{ width: 40 }}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortBy(product.options, (o) => o.order).map((calculationOption) => {
                              const optionDetails = details?.options.find((it) => it.id === calculationOption.id);
                              if (!optionDetails) return null;
                              const effectiveDisplayPrice = isNil(calculationOption.price)
                                ? calculationOption.priceListPrice
                                : new Price(calculationOption.price);
                              return (
                                <TableRow key={optionDetails.id}>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {optionDetails.name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        display: "block",
                                        whiteSpace: "pre-line",
                                      }}
                                    >
                                      {optionDetails.info?.polish}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {canEditPrice ? (
                                      <Button
                                        component="button"
                                        variant="text"
                                        sx={{
                                          padding: 0,
                                          minWidth: 0,
                                          textTransform: "none",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          setEditingOption({
                                            productIdx: idx,
                                            optionId: calculationOption.id,
                                          })
                                        }
                                      >
                                        {effectiveDisplayPrice.round(2).toDisplayValue(draft.currency)}
                                      </Button>
                                    ) : (
                                      <Typography variant="body2">
                                        {effectiveDisplayPrice.round(2).toDisplayValue(draft.currency)}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      type="number"
                                      disabled={isReadOnly}
                                      onFocus={(e) => e.target.select()}
                                      value={calculationOption.quantity}
                                      onChange={(e) =>
                                        handleQtyChange(
                                          idx,
                                          optionDetails.id,
                                          Math.max(0, toNumber(e.target.value) ?? 0),
                                        )
                                      }
                                      sx={{ width: 80, "& .MuiInputBase-root": { bgcolor: "#fff" } }}
                                      slotProps={{
                                        htmlInput: { min: 0, style: { textAlign: "center" } },
                                      }}
                                    />
                                  </TableCell>
                                  {hasDynamicPriceProducts && (
                                    <TableCell sx={{ width: 80, "& .MuiInputBase-root": { bgcolor: "#fff" } }}>
                                      {calculationOption.availableQuantity ?? "-"}
                                    </TableCell>
                                  )}
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={calculationOption.quantity > 0 ? "text.primary" : "text.disabled"}
                                      sx={{
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {effectiveDisplayPrice
                                        .multiply(calculationOption.quantity)
                                        .round(2)
                                        .toDisplayValue(draft.currency)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center" padding="none">
                                    <IconButton
                                      disabled={isReadOnly}
                                      size="small"
                                      sx={{ color: "text.secondary" }}
                                      onClick={() => handleRemoveOption(idx, optionDetails.id)}
                                      title="Usuń opcję z pakietu"
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            {!isReadOnly && (
                              <AddOptionRow
                                details={details}
                                existingOptionIds={product.options.map((o) => o.id)}
                                hideInternalOptions={context?.role === "CUSTOMER"}
                                onAdd={({ id }) => handleAddOption(idx, { id, quantity: 0 })}
                              />
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                );
              })}{" "}
            </Box>
          );
        })}
      </Stack>
      {!draft.packageId && !isReadOnly && (
        <AddExtraProduct
          onProductSelected={(p, date) =>
            handleAddProduct({ id: p.id, date: date ?? nextDate(startDate ?? dayjs(), products) })
          }
          currency={currency}
        />
      )}
      {infoDialogProduct && (
        <ProductInfoDialog
          product={{
            name: infoDialogProduct.name,
            providerProductType: infoDialogProduct.provider.productType,
            description: infoDialogProduct.description.polish,
          }}
          onClose={() => setInfoDialogProduct(undefined)}
        />
      )}
      {alternativesDialog.portal}
      <EditOptionPriceDialog
        editingOption={editingOption}
        productsWithPrices={productsWithPrices}
        onClose={() => setEditingOption(null)}
        onConfirm={handleOptionPriceChange}
      />
    </Grid>
  );
}

function AddExtraProduct(props: { onProductSelected(v: Product, date?: string): void; currency?: Currency }) {
  const [isAddingProduct, setIsAddingProduct] = useState(false);

  if (isAddingProduct) {
    return (
      <Paper elevation={0} sx={{ mt: 3, p: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mb: 2,
          }}
        >
          Wyszukaj produkt do dodania
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <ProductSelectAction
            onProductSelected={(p, date) => {
              props.onProductSelected(p, date);
              setIsAddingProduct(false);
            }}
            placeholder="Zacznij pisać, aby wyszukać produkt..."
            mapSelectionAvailable
            currency={props.currency}
          />
        </Box>
        <Button
          variant="text"
          color="inherit"
          sx={{ mt: 2, p: 0, fontWeight: "bold" }}
          onClick={() => setIsAddingProduct(false)}
        >
          Anuluj
        </Button>
      </Paper>
    );
  } else {
    return (
      <Button
        onClick={() => setIsAddingProduct(true)}
        startIcon={<AddCircleOutlineIcon />}
        variant="outlined"
        sx={{ mt: 3, borderRadius: 1 }}
      >
        Dodaj dodatkowy produkt
      </Button>
    );
  }
}

function EditOptionPriceDialog({
  editingOption,
  productsWithPrices,
  onClose,
  onConfirm,
}: {
  editingOption: { productIdx: number; optionId: string } | null;
  productsWithPrices: ReturnType<typeof useCalculationProducts>["productsWithPrices"];
  onClose: () => void;
  onConfirm: (productIdx: number, optionId: string, price: PriceValue) => void;
}) {
  if (!editingOption) return null;
  const product = productsWithPrices[editingOption.productIdx];
  const option = product?.options.find((o) => o.id === editingOption.optionId);
  if (!option) return null;
  return (
    <OptionPriceEditDialog
      productId={product.id}
      date={product.date}
      option={{ id: option.id, price: option.price }}
      onClose={onClose}
      onChange={(price) => onConfirm(editingOption.productIdx, editingOption.optionId, price)}
    />
  );
}
